import React, { useEffect, useMemo, useState } from 'react';
import { CheckboxMultiField } from 'Shared/form/CheckboxMultiField';
import { useFormikContext } from 'formik';
import { setWorkQueueFilterGenerator } from '../../workQueue.utils';
import { isEqual, sortBy } from 'lodash';
import useFetchPatientLabelsMenu from 'App/hooks/useFetchPatientLabelsMenu';
import AccordionWrapper from 'App/components/AccordionWrapper/AccordionWrapper';
import HeaderContent from '../HeaderContent';
import ExcludeToggleFilter from './ExcludeToggleFilter';
import { Button } from 'react-bootstrap';

const defaultLabelLength = 10;

const PatientLabelsFilterWithExcludeToggle = () => {
    const [showAllLabels, setShowAllLabels] = useState(false);
    const { data: availablePatientLabels } = useFetchPatientLabelsMenu();
    const {
        values: { filterParams, allPatientLabelsList },
        setFieldValue,
    } = useFormikContext();
    const { patientLabels, excludePatientLabels } = filterParams;
    const setWorkQueueFilter = setWorkQueueFilterGenerator(setFieldValue);

    const patientLabelOptions = useMemo(() => {
        const labels = (availablePatientLabels || []).map((opt) => ({ id: opt.valueName, option: opt.valueName }));
        return sortBy(labels, 'option');
    }, [availablePatientLabels]);

    // The shared list is seeded from the form's initial values, but the patient
    // labels menu can resolve after the form has mounted (e.g. on client-side
    // navigation with a stale cache). Keep the shared list in sync so the options
    // render without a page refresh and downstream filters stay correct.
    useEffect(() => {
        if (patientLabelOptions.length && !isEqual(patientLabelOptions, allPatientLabelsList)) {
            setFieldValue('allPatientLabelsList', patientLabelOptions);
        }
    }, [patientLabelOptions, allPatientLabelsList, setFieldValue]);

    const isExcludeDisabled = !patientLabels.length || patientLabels.length === allPatientLabelsList.length;
    const visibleLabels = useMemo(
        () => (showAllLabels ? allPatientLabelsList : allPatientLabelsList.slice(0, defaultLabelLength)),
        [allPatientLabelsList, showAllLabels]
    );
    const remainingLabelCount = allPatientLabelsList.length - defaultLabelLength;
    const showMoreToggleText = `${showAllLabels ? 'Hide' : 'Show'} ${remainingLabelCount} more...`;

    useEffect(() => {
        if (isExcludeDisabled && excludePatientLabels) {
            setFieldValue('filterParams.excludePatientLabels', false);
        }
    }, [isExcludeDisabled, excludePatientLabels, setFieldValue]);

    return (
        <AccordionWrapper
            title="Patient Labels"
            stateKey="workQueuePatientLabelsFilter-toggle"
            HeaderContent={HeaderContent}
            headerClassName="accordion-header-h4-title"
        >
            {allPatientLabelsList.length ? (
                <div className="multi-select-spacing-filter">
                    <ExcludeToggleFilter
                        fieldName="filterParams.excludePatientLabels"
                        disabled={isExcludeDisabled}
                        onChange={(checked) => setWorkQueueFilter(filterParams, 'excludePatientLabels', checked, true)}
                    />
                    <CheckboxMultiField
                        fieldName="filterParams.patientLabels"
                        value={patientLabels}
                        options={visibleLabels}
                        onChange={(value) => {
                            setWorkQueueFilter(filterParams, 'patientLabels', value, true);
                        }}
                    />
                    {remainingLabelCount > 0 && (
                        <Button
                            aria-label={showMoreToggleText}
                            variant="link"
                            className="show-more-text"
                            onClick={() => setShowAllLabels((prev) => !prev)}
                        >
                            {showMoreToggleText}
                        </Button>
                    )}
                </div>
            ) : null}
        </AccordionWrapper>
    );
};

export default PatientLabelsFilterWithExcludeToggle;
